import * as React from "react";

import { cn } from "@/lib/cn";

type ButtonVariant = "primary" | "outline" | "ghost";
type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
};

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  ({ className, variant = "primary", ...props }, ref) => {
    const base =
      // layout + typography
      "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium " +
      // transitions + motion
      "transition motion-reduce:transition-none will-change-transform active:translate-y-[0.5px] " +
      // accessibility (visible ring with offset against bg)
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] " +
      "focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--bg))] " +
      // disabled
      "disabled:opacity-60 disabled:cursor-not-allowed";

    const variants: Record<ButtonVariant, string> = {
      // use token-backed arbitrary color utilities
      primary: "bg-[hsl(var(--brand))] text-[hsl(var(--brand-fg))] hover:opacity-90",
      outline: "border border-border bg-card hover:bg-[hsl(var(--muted))] " + "text-fg",
      ghost: "bg-transparent hover:bg-[hsl(var(--muted))] " + "text-fg",
    };

    return <button ref={ref} className={cn(base, variants[variant], className)} {...props} />;
  },
);
Button.displayName = "Button";
